import React, { useEffect } from 'react';
import { FormattedNumber } from 'react-intl';
import { connect } from 'react-redux';
import { CATERING_PACKAGE_FORM } from '../../app-main/components/reduxForms/constants/formNames';
import { CateringDeliveryTimeSelect } from './CateringDeliveryTimeSelect';
import {
  calculateOrderTotal,
  getSelectedAddOns,
  getSelectedMenuItems
} from '../catering-utils';

const CateringOrderSummaryCmp = ({
  formValues,
  cateringPackage,
  booking,
  profile
}) => {
  const { selectedItems, addOns, cateringNotes } = formValues || {};
  const selectedMenuItems = getSelectedMenuItems(
    selectedItems,
    cateringPackage,
    booking?.number_of_guests
  );

  const selectedAddOns = getSelectedAddOns(addOns, booking?.number_of_guests);

  const estimatedTotal = calculateOrderTotal(
    cateringPackage,
    selectedMenuItems,
    selectedAddOns,
    booking?.number_of_guests
  );

  useEffect(() => {
    analytics.track('catering_order_summary_viewed', {
      booking_id: booking?._id,
      sso_id: profile?.data?.SSOid,
      activity_tag: booking?.tags?.activity,
      catering_package_id: cateringPackage?.id,
      order_total: parseFloat(estimatedTotal),
      add_ons: (selectedAddOns || []).map((addOn) => ({
        name: addOn.name,
        price: addOn.total
      }))
    });
  }, []);

  return (
    <div className="catering-order-summary">
      <CateringDeliveryTimeSelect
        bookingStart={booking?.start}
        bookingEnd={booking?.end}
      />

      <div className="catering-order-summary-table">
        <div className="flex middle space-between catering-order-summary-table-header">
          <span>Items</span>
          <span>Price</span>
        </div>

        <div className="catering-order-summary-table-body">
          <div className="flex middle space-between margin-lg-bottom">
            <span className="tw-headline-2-m">{cateringPackage.name}</span>
            {cateringPackage.price?.value > 0 &&
              !cateringPackage.price?.starts_at && (
                <span className="text-gray-600">
                  <FormattedNumber
                    value={
                      cateringPackage.price?.per_person
                        ? cateringPackage.price.value *
                          booking?.number_of_guests
                        : cateringPackage.price.value
                    }
                    currency="USD"
                    style="currency"
                    minimumFractionDigits={2}
                    maximumFractionDigits={2}
                  />
                </span>
              )}
          </div>

          {!!selectedMenuItems?.length && (
            <div className="catering-order-selections-wrap">
              {cateringPackage.price?.value > 0 && (
                <span className="tw-headline-1-m">
                  Your additional selections
                </span>
              )}
              {selectedMenuItems.map((item) => {
                let price =
                  item?.selectedSize?.price?.value || item.price?.value || 0;

                if (
                  item?.selectedSize?.price?.per_person ||
                  item.price?.per_person
                ) {
                  price *= booking?.number_of_guests;
                }

                return (
                  <div
                    key={item.name}
                    className="flex middle space-between text-gray-600 catering-summary-line-item"
                  >
                    <span>
                      {item.name}
                      {item.selectedSize && ` (${item.selectedSize.name})`}
                    </span>
                    {price > 0 && (
                      <span>
                        <FormattedNumber
                          value={price}
                          currency="USD"
                          style="currency"
                          minimumFractionDigits={2}
                          maximumFractionDigits={2}
                        />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!!selectedAddOns?.length && (
            <div className="catering-order-selections-wrap">
              <hr />
              <span className="tw-headline-1-m">Add-ons</span>
              {selectedAddOns.map((addOn) => {
                return (
                  <div
                    key={addOn.name}
                    className="flex middle space-between text-gray-600 catering-summary-line-item"
                  >
                    <span>
                      {addOn.title}
                      {addOn.count > 1 && ` x ${addOn.count}`}
                    </span>
                    {addOn.total > 0 && (
                      <span>
                        <FormattedNumber
                          value={addOn.total}
                          currency="USD"
                          style="currency"
                          minimumFractionDigits={2}
                          maximumFractionDigits={2}
                        />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <hr />

          <div className="flex middle space-between text-gray-600">
            <span>Taxes & delivery fee</span>
            <span className="text-right">Price calculated at checkout</span>
          </div>

          <hr />

          <div className="flex middle space-between tw-headline-1-m">
            <span>Estimated total</span>
            <span>
              <FormattedNumber
                value={estimatedTotal}
                currency="USD"
                style="currency"
                minimumFractionDigits={2}
                maximumFractionDigits={2}
              />
            </span>
          </div>
        </div>
      </div>

      {cateringNotes && (
        <div>
          <span className="tw-body-2-m">Special requests</span>
          <p className="no-margin">{cateringNotes}</p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ form, profile }) => ({
  formValues: form[CATERING_PACKAGE_FORM]?.values,
  profile
});

export const CateringOrderSummary = connect(mapStateToProps)(
  CateringOrderSummaryCmp
);
